import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCheck, UserX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { money, editionLabel, ENROLLMENT_STATUS } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

// Fix Y (2026-10-13): lista de una fecha para marcar quien asistio (instructor o area academica).
export default function AttendanceModal({ edition, course, open, onOpenChange }) {
  const { toast } = useToast();
  const [rows, setRows] = useState(null);
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const started = edition && edition.start_date <= todayIso;

  const load = useCallback(async () => {
    if (!edition) return;
    try {
      const { data } = await api.get("/academy/enrollments", { params: { edition_id: edition.id } });
      setRows(data.filter((x) => !["cancelled", "transferred"].includes(x.status)).sort((a, b) => a.student.name.localeCompare(b.student.name)));
    } catch (err) {
      setRows([]);
      toast({ title: "Error al cargar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [edition, toast]);

  useEffect(() => { if (open) { setRows(null); load(); } }, [open, load]);

  const mark = async (e, status) => {
    try {
      await api.post(`/academy/enrollments/${e.id}/attendance`, { status });
      load();
    } catch (err) {
      toast({ title: "No se pudo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={ACADEMY.attendanceModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Asistencia</DialogTitle>
          <DialogDescription>{course?.name} · {editionLabel(edition)}{started ? "" : " · se marca desde el día del curso"}</DialogDescription>
        </DialogHeader>
        {!rows ? <p className="text-sm text-muted-foreground">Cargando...</p> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay alumnos en esta fecha.</p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-lg">
            {rows.map((e) => {
              const st = ENROLLMENT_STATUS[e.status] || { label: e.status, cls: "" };
              return (
                <div key={e.id} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`${ACADEMY.attendanceRowPrefix}${e.id}`}>
                  <div className="flex-1 min-w-[160px]">
                    <Link to={`/academia/alumnos/${e.student.id}`} className="font-medium hover:underline" onClick={() => onOpenChange(false)}>{e.student.name}</Link>
                    <p className="text-xs text-muted-foreground">
                      {e.account.balance > 0 ? `Debe ${money(e.account.balance)}` : "Pagado"}
                      {["attended", "completed"].includes(e.status) ? ` · prácticas ${e.diploma.practices_done} de ${e.diploma.practices_required}` : ""}
                    </p>
                  </div>
                  <Badge className={`${st.cls} border-none font-normal`} data-testid={`${ACADEMY.attendanceStatusPrefix}${e.id}`}>{st.label}</Badge>
                  {started && ["pending", "confirmed"].includes(e.status) && (
                    <>
                      <Button size="sm" variant="outline" className="text-[#5B7A54]" onClick={() => mark(e, "attended")} data-testid={`${ACADEMY.attendanceAttendedPrefix}${e.id}`}><UserCheck className="w-4 h-4" /> Asistió</Button>
                      <Button size="sm" variant="outline" className="text-[#A34B49]" onClick={() => mark(e, "no_show")} data-testid={`${ACADEMY.attendanceNoShowPrefix}${e.id}`}><UserX className="w-4 h-4" /> No asistió</Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
